import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingBlock } from './Spinner';
import SortableTh, { type SortDirection } from './SortableTh';

/**
 * Per-(target × channel) scan-stats panel for Operations → Scans.
 *
 * Reads `GET /api/mgmt/scans/history` (backed by the `ScanHistory` table —
 * one row per IG/FB scan run, written by `lib/scan-engine.ts`). Surfaces the
 * `postsSeen / postsNew / postsAlreadySeen / newRatio` rollup so we can see
 * per-venue patterns and tune scan cadence empirically:
 *   - Sustained `newRatio = 0` over many scans  → bump cooldown / lower the
 *     per-venue post limit (we're spending money to re-see the same posts).
 *   - Sustained `newRatio ≥ 0.8`                → drop cooldown / raise the
 *     limit (we're missing posts between scans).
 *
 * No write actions in this panel — it's purely diagnostic; the actual
 * cooldown sliders live on the Settings page.
 */

type ScanHistoryChannel = 'instagram' | 'facebook';
type ScanHistoryTargetKind = 'venue' | 'series';

interface ScanHistoryRow {
  partitionKey: string;
  rowKey: string;
  scannedAt: string;
  channel: ScanHistoryChannel;
  targetKind: ScanHistoryTargetKind;
  targetId: string;
  targetName: string;
  postsSeen: number;
  postsNew: number;
  postsAlreadySeen: number;
  durationMs: number;
  error?: string;
  cooldownDays?: number;
}

interface TargetRollupRow {
  targetKind: ScanHistoryTargetKind;
  targetId: string;
  targetName: string;
  channel: ScanHistoryChannel;
  scans: number;
  postsSeen: number;
  postsNew: number;
  postsAlreadySeen: number;
  newRatio: number;
  avgPostsPerScan: number;
  errorScans: number;
  lastScannedAt: string;
}

interface ChannelRollupTotals {
  scans: number;
  postsSeen: number;
  postsNew: number;
  postsAlreadySeen: number;
  newRatio: number;
}

interface HistoryResponse {
  items: ScanHistoryRow[];
  itemsTruncated: boolean;
  totalRows: number;
  sinceDays: number;
  limit: number;
  channelRollup: Record<ScanHistoryChannel, ChannelRollupTotals>;
  targetRollup: TargetRollupRow[];
}

const WINDOWS: { value: number; label: string }[] = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' }
];

const CHANNEL_OPTIONS: { value: '' | ScanHistoryChannel; label: string }[] = [
  { value: '', label: 'IG + FB' },
  { value: 'instagram', label: 'Instagram only' },
  { value: 'facebook', label: 'Facebook only' }
];

const TARGET_OPTIONS: { value: '' | ScanHistoryTargetKind; label: string }[] = [
  { value: '', label: 'Venues + Series' },
  { value: 'venue', label: 'Venues only' },
  { value: 'series', label: 'Series only' }
];

// Sortable columns for the rollup table + their default direction (text = asc,
// counts/ratios = desc so the biggest values surface first).
type RollupSortKey =
  | 'target'
  | 'channel'
  | 'scans'
  | 'postsSeen'
  | 'postsNew'
  | 'postsAlreadySeen'
  | 'newRatio'
  | 'avgPostsPerScan'
  | 'errorScans';

const ROLLUP_DEFAULT_DIR: Record<RollupSortKey, SortDirection> = {
  target: 'asc',
  channel: 'asc',
  scans: 'desc',
  postsSeen: 'desc',
  postsNew: 'desc',
  postsAlreadySeen: 'desc',
  newRatio: 'desc',
  avgPostsPerScan: 'desc',
  errorScans: 'desc'
};

function compareRollup(a: TargetRollupRow, b: TargetRollupRow, key: RollupSortKey): number {
  switch (key) {
    case 'target':
      return (a.targetName || a.targetId).localeCompare(b.targetName || b.targetId);
    case 'channel':
      return a.channel.localeCompare(b.channel) || a.targetKind.localeCompare(b.targetKind);
    case 'scans':
      return a.scans - b.scans;
    case 'postsSeen':
      return a.postsSeen - b.postsSeen;
    case 'postsNew':
      return a.postsNew - b.postsNew;
    case 'postsAlreadySeen':
      return a.postsAlreadySeen - b.postsAlreadySeen;
    case 'newRatio':
      return a.newRatio - b.newRatio;
    case 'avgPostsPerScan':
      return a.avgPostsPerScan - b.avgPostsPerScan;
    case 'errorScans':
      return a.errorScans - b.errorScans;
  }
}

function formatPercent(ratio: number): string {
  if (!Number.isFinite(ratio)) return '—';
  return `${Math.round(ratio * 100)}%`;
}

/**
 * Suggest a cadence-tuning hint based on rollup data. This is intentionally
 * conservative — it's a hint, not an auto-action. Returns null when we don't
 * have enough scans to make a reliable suggestion.
 */
function cadenceHint(row: TargetRollupRow): { text: string; tone: 'warning' | 'info' } | null {
  if (row.scans < 3) return null;
  if (row.postsSeen === 0) {
    return { text: 'No posts returned — handle may be invalid or private', tone: 'warning' };
  }
  if (row.newRatio === 0) {
    return { text: 'All posts already seen — consider longer cooldown', tone: 'info' };
  }
  if (row.newRatio >= 0.8 && row.avgPostsPerScan >= 3) {
    return { text: 'Mostly new posts — consider shorter cooldown / larger fetch limit', tone: 'info' };
  }
  return null;
}

function ratioTier(row: { postsSeen: number; newRatio: number }): 'none' | 'low' | 'mid' | 'high' {
  if (row.postsSeen === 0) return 'none';
  if (row.newRatio >= 0.5) return 'high';
  if (row.newRatio >= 0.1) return 'mid';
  return 'low';
}

/**
 * New-ratio meter — a small horizontal bar (visual signal) paired with the
 * exact percentage text so meaning never relies on color alone. The fill
 * width is passed as a CSS custom property rather than an inline width so it
 * stays token-driven (see `.mh-ratio` in 07-admin.css).
 */
function RatioMeter({ ratio, postsSeen }: { ratio: number; postsSeen: number }) {
  const tier = ratioTier({ postsSeen, newRatio: ratio });
  const pct = Number.isFinite(ratio) ? Math.round(ratio * 100) : 0;
  // Floor any non-zero ratio to a visible 10% fill so 1–4% doesn't render as
  // an empty bar (the exact % is still shown as text alongside).
  const rounded = Math.round(pct / 10) * 10;
  const bucket = pct > 0 ? Math.max(10, Math.min(100, rounded)) : 0;
  const label = formatPercent(ratio);
  return (
    <span className={`mh-ratio mh-ratio--${tier}`} title={`${label} of posts were new`}>
      <span className="mh-ratio__track" aria-hidden="true">
        <span className={`mh-ratio__fill mh-ratio__fill--w${bucket}`} />
      </span>
      <span className="mh-ratio__value">{label}</span>
    </span>
  );
}

function ChannelPill({ channel }: { channel: ScanHistoryChannel }) {
  const icon = channel === 'instagram' ? 'instagram' : 'facebook';
  return (
    <span className={`mh-channel-pill mh-channel-pill--${channel}`}>
      <i className={`bi bi-${icon}`} aria-hidden="true" />
      {channel}
    </span>
  );
}

function targetLink(row: TargetRollupRow) {
  const path = row.targetKind === 'venue' ? `/venues/${row.targetId}` : `/series/${row.targetId}`;
  return (
    <Link to={path} className="text-decoration-none">
      {row.targetName || row.targetId}
    </Link>
  );
}

export default function ScanStatsPanel() {
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sinceDays, setSinceDays] = useState(30);
  const [channel, setChannel] = useState<'' | ScanHistoryChannel>('');
  const [targetKind, setTargetKind] = useState<'' | ScanHistoryTargetKind>('');
  const [search, setSearch] = useState('');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [sortKey, setSortKey] = useState<RollupSortKey>('postsNew');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const load = useMemo(
    () => () => {
      const params = new URLSearchParams();
      params.set('sinceDays', String(sinceDays));
      if (channel) params.set('channel', channel);
      if (targetKind) params.set('targetKind', targetKind);
      params.set('limit', '50'); // we only render the rollup; raw items unused here
      setErr(null);
      api
        .get<HistoryResponse>(`/api/mgmt/scans/history?${params.toString()}`)
        .then(setData)
        .catch((e) => setErr(String(e?.message ?? e)))
        .finally(() => setLoading(false));
    },
    [sinceDays, channel, targetKind]
  );

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const sortedRollup = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    const filtered = data.targetRollup.filter((row) => {
      if (errorsOnly && row.errorScans === 0) return false;
      if (needle && !(row.targetName || row.targetId).toLowerCase().includes(needle)) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered
      .map((row, idx) => ({ row, idx }))
      .sort((a, b) => {
        const cmp = compareRollup(a.row, b.row, sortKey);
        return cmp !== 0 ? cmp * dir : a.idx - b.idx; // stable tiebreak
      })
      .map((x) => x.row);
  }, [data, search, errorsOnly, sortKey, sortDir]);

  const toggleSort = (key: string) => {
    const k = key as RollupSortKey;
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(ROLLUP_DEFAULT_DIR[k]);
    }
  };

  const igTotals = data?.channelRollup.instagram;
  const fbTotals = data?.channelRollup.facebook;

  return (
    <section className="mb-4" aria-labelledby="scan-stats-heading">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h2 id="scan-stats-heading" className="h5 mb-0">Scan stats — posts seen vs new</h2>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={load}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <p className="mh-scan-intro small mb-3">
        How many Instagram and Facebook posts each venue / series returned, and how many were new
        vs already-seen. Use the <em>new ratio</em> to decide whether to scan more or less often.
      </p>

      <form
        className="mh-scan-toolbar row g-2 align-items-end mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <div className="col-sm-6 col-lg-3">
          <label htmlFor="stats-window" className="form-label small mb-1">Window</label>
          <select
            id="stats-window"
            className="form-select form-select-sm"
            value={sinceDays}
            onChange={(e) => setSinceDays(Number.parseInt(e.target.value, 10))}
          >
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 col-lg-3">
          <label htmlFor="stats-channel" className="form-label small mb-1">Channel</label>
          <select
            id="stats-channel"
            className="form-select form-select-sm"
            value={channel}
            onChange={(e) => setChannel(e.target.value as '' | ScanHistoryChannel)}
          >
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c.value || 'all'} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 col-lg-2">
          <label htmlFor="stats-target" className="form-label small mb-1">Target type</label>
          <select
            id="stats-target"
            className="form-select form-select-sm"
            value={targetKind}
            onChange={(e) => setTargetKind(e.target.value as '' | ScanHistoryTargetKind)}
          >
            {TARGET_OPTIONS.map((t) => (
              <option key={t.value || 'all'} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 col-lg-2">
          <label htmlFor="stats-search" className="form-label small mb-1">Search target</label>
          <input
            id="stats-search"
            type="search"
            className="form-control form-control-sm"
            placeholder="Name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-sm-6 col-lg-2 d-flex align-items-end">
          <div className="form-check mb-1">
            <input
              id="stats-errors-only"
              type="checkbox"
              className="form-check-input"
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
            />
            <label htmlFor="stats-errors-only" className="form-check-label small">Errors only</label>
          </div>
        </div>
      </form>

      {err && <div className="alert alert-danger" role="alert">{err}</div>}
      {loading && !data && <LoadingBlock label="Loading scan stats…" />}

      {data && (
        <>
          <div className="row g-3 mb-3" aria-label="Channel totals">
            {([
              ['instagram', 'Instagram', igTotals],
              ['facebook', 'Facebook', fbTotals]
            ] as const).map(([key, label, totals]) => {
              const ratio = totals?.newRatio ?? 0;
              return (
                <div key={key} className="col-md-6">
                  <div className="mh-channel-card">
                    <div className="mh-channel-card__head">
                      <span className={`mh-channel-card__icon mh-channel-card__icon--${key}`}>
                        <i className={`bi bi-${key}`} aria-hidden="true" />
                      </span>
                      <div className="me-auto">
                        <div className="mh-channel-card__title">{label}</div>
                        <div className="mh-channel-card__scans">
                          {(totals?.scans ?? 0).toLocaleString()} scan{(totals?.scans ?? 0) === 1 ? '' : 's'} · last {data.sinceDays}d
                        </div>
                      </div>
                      <RatioMeter ratio={ratio} postsSeen={totals?.postsSeen ?? 0} />
                    </div>
                    <div className="mh-channel-card__metrics">
                      <div>
                        <div className="mh-channel-card__metric-value">{(totals?.postsSeen ?? 0).toLocaleString()}</div>
                        <div className="mh-channel-card__metric-label">Seen</div>
                      </div>
                      <div>
                        <div className="mh-channel-card__metric-value mh-channel-card__metric-value--new">{(totals?.postsNew ?? 0).toLocaleString()}</div>
                        <div className="mh-channel-card__metric-label">New</div>
                      </div>
                      <div>
                        <div className="mh-channel-card__metric-value">{(totals?.postsAlreadySeen ?? 0).toLocaleString()}</div>
                        <div className="mh-channel-card__metric-label">Already</div>
                      </div>
                      <div>
                        <div className="mh-channel-card__metric-value">{formatPercent(ratio)}</div>
                        <div className="mh-channel-card__metric-label">New %</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {sortedRollup.length === 0 ? (
            <div className="mh-scan-empty">
              <i className="bi bi-bar-chart-line" aria-hidden="true" />
              <p className="mb-0 small">
                {(search.trim() || errorsOnly)
                  ? 'No target × channel pairs match the current filters.'
                  : 'No scan history rows in the selected window. New rows will appear after the next IG/FB scan.'}
              </p>
            </div>
          ) : (
            <div className="mh-scan-table-wrap">
              <table className="table table-sm align-middle mh-scan-table">
                <caption className="small text-body-secondary px-2">
                  {sortedRollup.length.toLocaleString()} target × channel pairs · last {data.sinceDays} days · {data.totalRows.toLocaleString()} scan rows
                </caption>
                <thead>
                  <tr>
                    <SortableTh label="Target" sortKey="target" activeKey={sortKey} direction={sortDir} onSort={toggleSort} />
                    <SortableTh label="Channel" sortKey="channel" activeKey={sortKey} direction={sortDir} onSort={toggleSort} />
                    <SortableTh label="Scans" sortKey="scans" activeKey={sortKey} direction={sortDir} onSort={toggleSort} align="end" />
                    <SortableTh label="Seen" sortKey="postsSeen" activeKey={sortKey} direction={sortDir} onSort={toggleSort} align="end" />
                    <SortableTh label="New" sortKey="postsNew" activeKey={sortKey} direction={sortDir} onSort={toggleSort} align="end" />
                    <SortableTh label="Already" sortKey="postsAlreadySeen" activeKey={sortKey} direction={sortDir} onSort={toggleSort} align="end" />
                    <SortableTh label="New ratio" sortKey="newRatio" activeKey={sortKey} direction={sortDir} onSort={toggleSort} />
                    <SortableTh label="Avg/scan" sortKey="avgPostsPerScan" activeKey={sortKey} direction={sortDir} onSort={toggleSort} align="end" />
                    <SortableTh label="Errors" sortKey="errorScans" activeKey={sortKey} direction={sortDir} onSort={toggleSort} align="end" />
                    <th scope="col">Hint</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRollup.map((row) => {
                    const hint = cadenceHint(row);
                    return (
                      <tr key={`${row.targetKind}:${row.targetId}:${row.channel}`}>
                        <td>{targetLink(row)}</td>
                        <td>
                          <ChannelPill channel={row.channel} />
                          <span className="text-body-secondary small ms-1">{row.targetKind}</span>
                        </td>
                        <td className="text-end">{row.scans.toLocaleString()}</td>
                        <td className="text-end">{row.postsSeen.toLocaleString()}</td>
                        <td className="text-end fw-semibold">{row.postsNew.toLocaleString()}</td>
                        <td className="text-end">{row.postsAlreadySeen.toLocaleString()}</td>
                        <td>
                          <RatioMeter ratio={row.newRatio} postsSeen={row.postsSeen} />
                        </td>
                        <td className="text-end">{row.avgPostsPerScan.toFixed(1)}</td>
                        <td className="text-end">
                          {row.errorScans > 0 ? (
                            <span className="mh-scan-badge mh-scan-badge--error">
                              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                              {row.errorScans}
                            </span>
                          ) : (
                            <span className="text-body-secondary">0</span>
                          )}
                        </td>
                        <td>
                          {hint ? (
                            <span className={`small ${hint.tone === 'warning' ? 'text-warning-emphasis' : 'text-body-secondary'}`}>
                              {hint.text}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
